import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Paper,
  Tooltip,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Security as SecurityIcon,
  Description as DocumentIcon,
  Rule as RuleIcon,
  Lightbulb as RecommendationIcon,
  CloudUpload as UploadIcon,
  Verified as VerifiedIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import correlationService, { ComprehensiveCorrelationResponse } from '../../features/cross-document/correlationService';

interface ATLASValidationPanelProps {
  validationResult: ComprehensiveCorrelationResponse;
}

export const ATLASValidationPanel: React.FC<ATLASValidationPanelProps> = ({ validationResult }) => {
  const [expandedSection, setExpandedSection] = useState<string | false>('summary');

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const { atlas_validation } = validationResult;

  const getRecommendationIcon = (recommendation: string) => {
    const icon = correlationService.getRecommendationIcon(recommendation);
    switch (icon) {
      case 'CloudUpload':
        return <UploadIcon fontSize="small" />;
      case 'Verified':
        return <VerifiedIcon fontSize="small" />;
      case 'Build':
        return <BuildIcon fontSize="small" />;
      default:
        return <RecommendationIcon fontSize="small" />;
    }
  };

  const getRecommendationColor = (recommendation: string) => {
    const priority = correlationService.getRecommendationPriority(recommendation);
    switch (priority) {
      case 'high':
        return '#F44336';
      case 'medium':
        return '#FF9800';
      case 'low':
        return '#2196F3';
      default:
        return '#757575';
    }
  };

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SecurityIcon color="primary" />
          <Typography variant="h6">ATLAS Validation</Typography>
        </Box>

        {/* Summary */}
        <Accordion expanded={expandedSection === 'summary'} onChange={handleAccordionChange('summary')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#F5F5F5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              {atlas_validation.can_submit ? (
                <CheckCircleIcon sx={{ color: '#4CAF50' }} />
              ) : (
                <ErrorIcon sx={{ color: '#F44336' }} />
              )}
              <Typography variant="subtitle1" fontWeight="bold">
                Submission Status
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <Chip
                  label={atlas_validation.can_submit ? 'Ready to Submit' : 'Cannot Submit'}
                  color={atlas_validation.can_submit ? 'success' : 'error'}
                  size="small"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2 }}>
              {atlas_validation.can_submit ? (
                <Alert severity="success">
                  All ATLAS requirements are satisfied. You can proceed with submission to BEO ATLAS.
                </Alert>
              ) : (
                <Alert severity="error">
                  ATLAS validation failed. Please review and fix the issues below before submitting.
                </Alert>
              )}
            </Box>

            {/* Key metrics */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: '#FAFAFA' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Declaration Type
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {atlas_validation.declaration_type.toUpperCase()}
                </Typography>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, backgroundColor: '#FAFAFA' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Mandatory Documents
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color={atlas_validation.document_requirements.all_mandatory_present ? 'success' : 'error'}
                >
                  {atlas_validation.document_requirements.all_mandatory_present ? 'All Present' : 'Missing'}
                </Typography>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, backgroundColor: '#FAFAFA' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Correlation Rules
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color={atlas_validation.correlation_rules.rules_failed === 0 ? 'success' : 'error'}
                >
                  {atlas_validation.correlation_rules.rules_passed}/{atlas_validation.correlation_rules.total_rules_checked} Passed
                </Typography>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, backgroundColor: '#FAFAFA' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Issues Found
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" color={atlas_validation.issues.length > 0 ? 'error' : 'success'}>
                  {atlas_validation.issues.length}
                </Typography>
              </Paper>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Document Requirements */}
        <Accordion
          expanded={expandedSection === 'documents'}
          onChange={handleAccordionChange('documents')}
          sx={{ mt: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#F5F5F5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DocumentIcon />
              <Typography variant="subtitle1" fontWeight="bold">
                Document Requirements
              </Typography>
              {!atlas_validation.document_requirements.all_mandatory_present && (
                <Chip label="Issues" color="error" size="small" />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Missing documents alert */}
            {atlas_validation.document_requirements.missing_mandatory.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Missing Mandatory Documents:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {atlas_validation.document_requirements.missing_mandatory.map((doc, index) => (
                    <li key={index}>
                      <Typography variant="body2">{doc}</Typography>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {/* Present documents list */}
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Document Status:
            </Typography>
            <List dense>
              {atlas_validation.document_requirements.present_documents.map((doc, index) => {
                const isOk = doc.status === 'ok';
                const isMissing = doc.status === 'missing';

                return (
                  <ListItem
                    key={index}
                    sx={{
                      backgroundColor: isOk ? '#E8F5E9' : isMissing ? '#FFEBEE' : '#FFF3E0',
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemIcon>
                      {isOk ? (
                        <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                      ) : isMissing ? (
                        <CancelIcon sx={{ color: '#F44336' }} />
                      ) : (
                        <WarningIcon sx={{ color: '#FF9800' }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {doc.document_type.replace(/_/g, ' ').toUpperCase()}
                          </Typography>
                          <Chip label={`Count: ${doc.count}`} size="small" variant="outlined" />
                          <Chip
                            label={doc.status.toUpperCase()}
                            size="small"
                            color={isOk ? 'success' : isMissing ? 'error' : 'warning'}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Correlation Rules */}
        <Accordion expanded={expandedSection === 'rules'} onChange={handleAccordionChange('rules')} sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#F5F5F5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RuleIcon />
              <Typography variant="subtitle1" fontWeight="bold">
                Correlation Rules
              </Typography>
              {atlas_validation.correlation_rules.rules_failed > 0 && (
                <Chip label={`${atlas_validation.correlation_rules.rules_failed} Failed`} color="error" size="small" />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Rules progress */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Rules Passed:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {atlas_validation.correlation_rules.rules_passed} / {atlas_validation.correlation_rules.total_rules_checked}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  (atlas_validation.correlation_rules.rules_passed / atlas_validation.correlation_rules.total_rules_checked) * 100
                }
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: '#FFEBEE',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: atlas_validation.correlation_rules.rules_failed === 0 ? '#4CAF50' : '#FF9800',
                  },
                }}
              />
            </Box>

            {/* Failed rules */}
            {atlas_validation.correlation_rules.failed_rules.length > 0 ? (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {atlas_validation.correlation_rules.failed_rules.length} correlation rule
                  {atlas_validation.correlation_rules.failed_rules.length > 1 ? 's' : ''} failed
                </Alert>

                {atlas_validation.correlation_rules.failed_rules.map((failedRule, index) => (
                  <Paper key={index} elevation={1} sx={{ p: 2, mb: 2, borderLeft: '4px solid #F44336' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      {failedRule.error_message}
                    </Typography>

                    <Box sx={{ pl: 2, mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Document:</strong> {failedRule.document}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Rule:</strong> {failedRule.rule?.primary_doc_type} must correlate with{' '}
                        {failedRule.rule?.required_doc_types?.join(', ') || '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Required Criteria:</strong> {failedRule.rule?.correlation_criteria?.join(', ') || '—'}
                      </Typography>
                      <Typography variant="body2" color="error.main" gutterBottom>
                        <strong>Reason:</strong> {failedRule.reason}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </>
            ) : (
              <Alert severity="success">All correlation rules passed ✓</Alert>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Issues */}
        {atlas_validation.issues.length > 0 && (
          <Accordion expanded={expandedSection === 'issues'} onChange={handleAccordionChange('issues')} sx={{ mt: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#FFEBEE' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon sx={{ color: '#F44336' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Issues ({atlas_validation.issues.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="error" sx={{ mb: 2 }}>
                You must fix these issues before submitting to ATLAS
              </Alert>

              <List dense>
                {atlas_validation.issues.map((issue, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      backgroundColor: '#FFEBEE',
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemIcon>
                      <ErrorIcon sx={{ color: '#F44336' }} />
                    </ListItemIcon>
                    <ListItemText primary={<Typography variant="body2">{issue}</Typography>} />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Recommendations */}
        {atlas_validation.recommendations.length > 0 && (
          <Accordion
            expanded={expandedSection === 'recommendations'}
            onChange={handleAccordionChange('recommendations')}
            sx={{ mt: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#E3F2FD' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RecommendationIcon sx={{ color: '#2196F3' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Recommendations ({atlas_validation.recommendations.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" sx={{ mb: 2 }}>
                Follow these recommendations to fix the issues and improve correlation
              </Alert>

              <List dense>
                {atlas_validation.recommendations.map((recommendation, index) => {
                  const priority = correlationService.getRecommendationPriority(recommendation);
                  const color = getRecommendationColor(recommendation);

                  return (
                    <ListItem
                      key={index}
                      sx={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: 1,
                        mb: 1,
                        borderLeft: `4px solid ${color}`,
                      }}
                    >
                      <ListItemIcon>{getRecommendationIcon(recommendation)}</ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {recommendation}
                            </Typography>
                            <Chip
                              label={priority.toUpperCase()}
                              size="small"
                              color={priority === 'high' ? 'error' : priority === 'medium' ? 'warning' : 'info'}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
};

export default ATLASValidationPanel;
